<script setup lang="ts">
/**
 * [#022fa07d] Публичная инструкция «Постановка iPhone на контроль» — /faq/web.
 * Без авторизации (meta.requiresAuth нет), переключатель RU/UZ в хедере.
 * Скрины с красными выделениями лежат в public/faq-web/{ru,uz}/ — это
 * готовые аннотированные картинки (генерятся из методички проекта).
 */
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { Globe, Smartphone, Laptop, ChevronRight } from 'lucide-vue-next';
import '@/assets/main.css';
import PublicShell from './landing/PublicShell.vue';
import { usePublicPage } from './landing/usePublicPage';
import FaqCarousel from '@/components/FaqCarousel.vue';

const { locale, copy: pageCopy, to } = usePublicPage();
const lang = computed<string>(() => (locale.value in CONTENT ? locale.value : 'ru'));
const assetsLocale = () => (locale.value === 'uz' ? 'uz' : 'ru');


type Kind = 'web' | 'device' | 'mac';

interface Step {
  kind: Kind;
  title: string;
  /** HTML: допускается <b> и <span class="warn"> — контент свой, не пользовательский. */
  html: string;
  images: string[];
  grid: 'web' | 'ios' | 'cfg';
}

interface Content {
  title: string;
  subtitle: string;
  org: string;
  legend: string;
  chips: Record<Kind, string>;
  transitions: Record<Kind, string>;
  steps: Step[];
  footer: string;
}

const CONTENT: Record<string, Content> = {
  ru: {
    title: 'Постановка iPhone на контроль',
    subtitle: 'Панель → iPhone → кабель + Apple Configurator → активация → «На контроле»',
    org: 'Организация: TSDQWISH, MChJ · Время: 5–8 минут на устройство',
    legend: 'Красным на скринах показано, куда нажимать и что вводить. Цветная плашка перед шагом — куда перейти: браузер, iPhone или мак.',
    chips: { web: 'БРАУЗЕР · АДМИН-ПАНЕЛЬ', device: 'IPHONE · В РУКАХ', mac: 'MAC · APPLE CONFIGURATOR' },
    transitions: { web: 'Перейдите в браузер — админ-панель', device: 'Возьмите iPhone в руки', mac: 'Перейдите на мак — Apple Configurator' },
    steps: [
      { kind: 'web', grid: 'web', title: 'Продажа в панели', html: 'Выберите модель устройства, впишите серийный номер с коробки и нажмите <span class="press">«Поставить на контроль»</span>.', images: ['s1'] },
      { kind: 'web', grid: 'web', title: 'Карточка устройства', html: 'Устройство принято. Нажмите <span class="press">«Карточка устройства»</span> и держите её открытой до конца.', images: ['s2'] },
      { kind: 'web', grid: 'web', title: 'Шаг 1 из 2 в карточке', html: 'Откроется карточка устройства с инструкцией и статусом. Оставьте этот экран открытым и идите к следующему шагу.', images: ['s3'] },
      { kind: 'device', grid: 'ios', title: 'Дойдите до «Данные и конфиденциальность»', html: 'Возьмите новый или сброшенный iPhone. Пройдите экраны по порядку. На экране «Данные и конфиденциальность» — <span class="warn">STOP! Ничего не нажимайте.</span>', images: ['s4-1', 's4-2', 's4-3', 's4-4', 's4-5', 's4-6'] },
      { kind: 'mac', grid: 'cfg', title: 'Подключите устройство к макбуку', html: 'Подключите iPhone кабелем к макбуку и откройте Apple Configurator. Выделите iPhone (1) и нажмите шестерёнку (2). Дальше во всех окнах жмите <span class="press">Next</span>, ничего не меняя. В последнем окне — <span class="press">Prepare</span>.', images: ['s5-1', 's5-2', 's5-3', 's5-4', 's5-5', 's5-6'] },
      { kind: 'mac', grid: 'web', title: 'Ожидание перезагрузки телефона', html: 'Ждите, кабель не отключайте. iPhone сам сотрётся и полностью перезагрузится. Когда появится первый экран «привет» — идите на следующий этап.', images: ['s6'] },
      { kind: 'web', grid: 'web', title: 'Ожидание «Шаг 2 из 2» в карточке устройства', html: 'Ждите, когда в карточке появится «Шаг 2 из 2 · Можно активировать устройство». <span class="warn">ВАЖНО! На следующий этап можно идти, только когда появится надпись «Можно активировать устройство».</span>', images: ['s7'] },
      { kind: 'device', grid: 'ios', title: 'Активация и регистрация', html: 'Снова пройдите экраны по порядку. В конце нажмите <span class="press">«Зарегистрировать этот iPhone»</span>.', images: ['s8-1', 's8-2', 's8-3', 's8-4', 's8-5'] },
      { kind: 'web', grid: 'web', title: 'Ожидайте, пока устройство встанет на контроль', html: 'Ждите, когда в карточке появится зелёный бейдж «На контроле». Готово — передавайте телефон клиенту.', images: ['s9'] },
    ],
    footer: 'TSDQWISH MChJ · Инструкция для продавцов',
  },
  uz: {
    title: "iPhone'ni nazoratga qo'yish",
    subtitle: "Panel → iPhone → kabel + Apple Configurator → aktivatsiya → «Nazoratda»",
    org: "Tashkilot: TSDQWISH, MChJ · Vaqt: har bir qurilmaga 5–8 daqiqa",
    legend: "Skrinlardagi qizil ramka — qayerni bosish va nima kiritishni ko'rsatadi. Qadam oldidagi rangli plashka — qayerga o'tish: brauzer, iPhone yoki mac.",
    chips: { web: 'BRAUZER · ADMIN-PANEL', device: "IPHONE · QO'LINGIZDA", mac: 'MAC · APPLE CONFIGURATOR' },
    transitions: { web: "Brauzerga o'ting — admin-panel", device: "iPhone'ni qo'lingizga oling", mac: "Mac'ga o'ting — Apple Configurator" },
    steps: [
      { kind: 'web', grid: 'web', title: 'Paneldagi sotuv', html: 'Qurilma modelini tanlang, quti ustidagi seriya raqamini kiriting va <span class="press">«Nazoratga qo\'yish»</span> tugmasini bosing.', images: ['s1'] },
      { kind: 'web', grid: 'web', title: 'Qurilma kartasi', html: 'Qurilma qabul qilindi. <span class="press">«Qurilma kartasi»</span> tugmasini bosing va kartani oxirigacha ochiq qoldiring.', images: ['s2'] },
      { kind: 'web', grid: 'web', title: 'Kartadagi 1-qadam / 2', html: "Qurilma kartasi ochiladi: yo'riqnoma va jonli status shu yerda. Bu ekranni ochiq qoldiring va keyingi qadamga o'ting.", images: ['s3'] },
      { kind: 'device', grid: 'ios', title: "«Данные и конфиденциальность» ekranigacha boring", html: "Yangi yoki to'liq tozalangan iPhone oling. Ekranlarni tartib bilan o'ting. «Данные и конфиденциальность» ekranida — <span class=\"warn\">STOP! Hech narsa bosmang.</span>", images: ['s4-1', 's4-2', 's4-3', 's4-4', 's4-5', 's4-6'] },
      { kind: 'mac', grid: 'cfg', title: "Qurilmani macbook'ka ulang", html: 'iPhone\'ni kabel bilan macbook\'ka ulang va Apple Configurator\'ni oching. iPhone\'ni belgilang va tishli tugmani bosing. Keyin hamma oynada <span class="press">Next</span> bosing, hech narsa o\'zgartirmang. Oxirgi oynada — <span class="press">Prepare</span>.', images: ['s5-1', 's5-2', 's5-3', 's5-4', 's5-5', 's5-6'] },
      { kind: 'mac', grid: 'web', title: 'Telefon qayta yonishini kuting', html: "Kuting, kabelni uzmang. iPhone o'zi tozalanadi va to'liq qayta yonadi. Birinchi «привет» ekrani chiqqanda — keyingi bosqichga o'ting.", images: ['s6'] },
      { kind: 'web', grid: 'web', title: "Kartada «2-qadam / 2»ni kuting", html: 'Kartada «2-qadam / 2 · Qurilmani aktivlashtirsa bo\'ladi» chiqishini kuting. <span class="warn">MUHIM! Keyingi bosqichga faqat «Qurilmani aktivlashtirsa bo\'ladi» yozuvi chiqqandan keyin o\'ting.</span>', images: ['s7'] },
      { kind: 'device', grid: 'ios', title: "Aktivatsiya va ro'yxatga olish", html: 'Ekranlarni yana tartib bilan o\'ting. Oxirida <span class="press">«Зарегистрировать этот iPhone»</span> tugmasini bosing.', images: ['s8-1', 's8-2', 's8-3', 's8-4', 's8-5'] },
      { kind: 'web', grid: 'web', title: 'Qurilma nazoratga chiqishini kuting', html: "Kartada yashil «Nazoratda» belgisi chiqishini kuting. Tayyor — telefonni mijozga topshiring.", images: ['s9'] },
    ],
    footer: "TSDQWISH MChJ · Sotuvchilar uchun yo'riqnoma",
  },
  en: {
    title: "Putting an iPhone Under Control",
    subtitle: "Admin panel → iPhone → cable + Apple Configurator → activation → “Under control”",
    org: "Organization: TSDQWISH, MChJ · Time: 5–8 minutes per device",
    legend: "Red marks on the screenshots show where to tap and what to enter. The colored bar before each step shows where to go: browser, iPhone, or Mac.",
    chips: { web: "BROWSER · ADMIN PANEL", device: "IPHONE · IN HAND", mac: "MAC · APPLE CONFIGURATOR" },
    transitions: { web: "Switch to the browser — admin panel", device: "Take the iPhone in hand", mac: "Switch to the Mac — Apple Configurator" },
    steps: [
      { kind: 'web', grid: 'web', title: "Sale in the admin panel", html: "Select the device model, enter the serial number from the box, and press <span class=\"press\">“Put Under Control”</span>.", images: ['s1'] },
      { kind: 'web', grid: 'web', title: "Device page", html: "The device has been accepted. Press <span class=\"press\">“Device Page”</span> and keep it open until the end.", images: ['s2'] },
      { kind: 'web', grid: 'web', title: "Step 1 of 2 on the device page", html: "The device page opens with instructions and status. Leave this screen open and go to the next step.", images: ['s3'] },
      { kind: 'device', grid: 'ios', title: "Get to the “Data & Privacy” screen", html: "Take a new or erased iPhone. Go through the screens in order. On the “Data & Privacy” screen — <span class=\"warn\">STOP! Don't tap anything.</span>", images: ['s4-1', 's4-2', 's4-3', 's4-4', 's4-5', 's4-6'] },
      { kind: 'mac', grid: 'cfg', title: "Connect the device to the MacBook", html: "Connect the iPhone to the MacBook with a cable and open Apple Configurator. Select the iPhone (1) and press the gear icon (2). Then press <span class=\"press\">Next</span> in every window without changing anything. In the last window — <span class=\"press\">Prepare</span>.", images: ['s5-1', 's5-2', 's5-3', 's5-4', 's5-5', 's5-6'] },
      { kind: 'mac', grid: 'web', title: "Waiting for the phone to restart", html: "Wait, do not unplug the cable. The iPhone will erase itself and fully restart. When the first “Hello” screen appears — move to the next step.", images: ['s6'] },
      { kind: 'web', grid: 'web', title: "Waiting for “Step 2 of 2” on the device page", html: "Wait for “Step 2 of 2 · Device can be activated” to appear on the device page. <span class=\"warn\">IMPORTANT! Move to the next step only once “Device can be activated” appears.</span>", images: ['s7'] },
      { kind: 'device', grid: 'ios', title: "Activation and enrollment", html: "Go through the screens in order again. At the end, press <span class=\"press\">“Enroll This iPhone”</span>.", images: ['s8-1', 's8-2', 's8-3', 's8-4', 's8-5'] },
      { kind: 'web', grid: 'web', title: "Wait for the device to come under control", html: "Wait for the green “Under control” badge to appear on the device page. Done — hand the phone to the customer.", images: ['s9'] },
    ],
    footer: "TSDQWISH MChJ · Guide for sellers",
  },
  es: {
    title: "Inscripción de un iPhone",
    subtitle: "Panel → iPhone → cable + Apple Configurator → activación → «Inscrito»",
    org: "Organización: TSDQWISH, MChJ · Tiempo: 5–8 minutos por dispositivo",
    legend: "Las marcas rojas en las capturas muestran dónde pulsar y qué introducir. La franja de color antes de cada paso indica adónde ir: navegador, iPhone o Mac.",
    chips: { web: "NAVEGADOR · PANEL DE ADMINISTRACIÓN", device: "IPHONE · EN LA MANO", mac: "MAC · APPLE CONFIGURATOR" },
    transitions: { web: "Vaya al navegador — panel de administración", device: "Tome el iPhone en la mano", mac: "Vaya al Mac — Apple Configurator" },
    steps: [
      { kind: 'web', grid: 'web', title: "Venta en el panel", html: "Seleccione el modelo del dispositivo, introduzca el número de serie de la caja y pulse <span class=\"press\">«Inscribir»</span>.", images: ['s1'] },
      { kind: 'web', grid: 'web', title: "Ficha del dispositivo", html: "El dispositivo ha sido aceptado. Pulse <span class=\"press\">«Ficha del dispositivo»</span> y manténgala abierta hasta el final.", images: ['s2'] },
      { kind: 'web', grid: 'web', title: "Paso 1 de 2 en la ficha", html: "Se abrirá la ficha del dispositivo con las instrucciones y el estado. Deje esta pantalla abierta y pase al siguiente paso.", images: ['s3'] },
      { kind: 'device', grid: 'ios', title: "Llegue a la pantalla «Datos y privacidad»", html: "Tome un iPhone nuevo o borrado. Pase las pantallas en orden. En la pantalla «Datos y privacidad» — <span class=\"warn\">¡ALTO! No pulse nada.</span>", images: ['s4-1', 's4-2', 's4-3', 's4-4', 's4-5', 's4-6'] },
      { kind: 'mac', grid: 'cfg', title: "Conecte el dispositivo al MacBook", html: "Conecte el iPhone al MacBook con un cable y abra Apple Configurator. Seleccione el iPhone (1) y pulse el icono del engranaje (2). Después pulse <span class=\"press\">Next</span> en todas las ventanas sin cambiar nada. En la última ventana — <span class=\"press\">Prepare</span>.", images: ['s5-1', 's5-2', 's5-3', 's5-4', 's5-5', 's5-6'] },
      { kind: 'mac', grid: 'web', title: "Espera del reinicio del teléfono", html: "Espere, no desconecte el cable. El iPhone se borrará por sí solo y se reiniciará por completo. Cuando aparezca la primera pantalla «Hola» — pase a la siguiente etapa.", images: ['s6'] },
      { kind: 'web', grid: 'web', title: "Espera del «Paso 2 de 2» en la ficha del dispositivo", html: "Espere a que en la ficha aparezca «Paso 2 de 2 · Se puede activar el dispositivo». <span class=\"warn\">¡IMPORTANTE! Pase a la siguiente etapa solo cuando aparezca el texto «Se puede activar el dispositivo».</span>", images: ['s7'] },
      { kind: 'device', grid: 'ios', title: "Activación e inscripción", html: "Pase de nuevo las pantallas en orden. Al final pulse <span class=\"press\">«Inscribir este iPhone»</span>.", images: ['s8-1', 's8-2', 's8-3', 's8-4', 's8-5'] },
      { kind: 'web', grid: 'web', title: "Espere a que el dispositivo quede inscrito", html: "Espere a que en la ficha aparezca la insignia verde «Inscrito». Listo — entregue el teléfono al cliente.", images: ['s9'] },
    ],
    footer: "TSDQWISH MChJ · Guía para vendedores",
  },
};

const kindIcon: Record<Kind, typeof Globe> = { web: Globe, device: Smartphone, mac: Laptop };

const transClass: Record<Kind, string> = { web: 'lp-kind--web', device: 'lp-kind--device', mac: 'lp-kind--mac' };
const chipClass: Record<Kind, string> = { web: 'lp-kind--web', device: 'lp-kind--device', mac: 'lp-kind--mac' };

function img(name: string) {
  return `/faq-web/${assetsLocale()}/${name}.webp`; // свои скрины есть только у ru/uz
}
</script>

<template>
  <PublicShell>

    <main class="lp-wrap lp-guide">
      <nav class="lp-crumbs" :aria-label="pageCopy.articles.home">
        <RouterLink :to="to('/')">{{ pageCopy.articles.home }}</RouterLink>
        <ChevronRight :size="14" />
        <RouterLink :to="to('/articles')">{{ pageCopy.articles.title }}</RouterLink>
      </nav>
      <!-- Заголовок -->
      <div class="lp-page-head">
        <h1 >{{ CONTENT[lang].title }}</h1>
        <p >{{ CONTENT[lang].subtitle }}</p>
        <p class="lp-guide-org">{{ CONTENT[lang].org }}</p>
      </div>

      <div class="lp-guide-legend">
        {{ CONTENT[lang].legend }}
      </div>

      <!-- Шаги -->
      <template v-for="(step, i) in CONTENT[lang].steps" :key="`${lang}-${i}`">
        <!-- Плашка перехода при смене контекста -->
        <div
          v-if="i === 0 || CONTENT[lang].steps[i - 1].kind !== step.kind"
          class="lp-guide-trans"
          :class="transClass[step.kind]"
        >
          <component :is="kindIcon[step.kind]" :size="26" class="shrink-0" />
          {{ CONTENT[lang].transitions[step.kind] }}
        </div>

        <section class="lp-card lp-guide-step">
          <div class="lp-guide-step-head">
            <span class="lp-num">{{ i + 1 }}</span>
            <h2 >{{ step.title }}</h2>
            <span class="lp-guide-chip" :class="chipClass[step.kind]">
              {{ CONTENT[lang].chips[step.kind] }}
            </span>
          </div>
          <!-- Контент свой (не пользовательский): warn-подсветка через v-html -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p class="lp-guide-text faq-step-text" v-html="step.html" />

          <template v-if="step.grid === 'cfg'">
            <img :src="img(step.images[0])" :alt="step.title" loading="lazy" class="w-full lp-guide-img mb-3" />
            <!-- Слайдер во всю ширину браузера (full-bleed за пределы контентной колонки) -->
            <div class="w-screen relative left-1/2 -translate-x-1/2 px-3 sm:px-6">
              <FaqCarousel :images="step.images.slice(1).map((n) => img(n))" />
            </div>
          </template>
          <!-- iOS-кадры: на desktop все в один ряд (6 → 6 колонок, 5 → 5 колонок) -->
          <div
            v-else
            :class="step.grid === 'ios' ? (step.images.length === 6 ? 'grid grid-cols-3 sm:grid-cols-6 gap-3' : 'grid grid-cols-3 sm:grid-cols-5 gap-3') : ''"
          >
            <img
              v-for="(name, j) in step.images"
              :key="name"
              :src="img(name)"
              :alt="`${step.title} ${j + 1}`"
              loading="lazy"
              class="w-full lp-guide-img"
            />
          </div>
        </section>
      </template>

      <footer class="lp-guide-foot">{{ CONTENT[lang].footer }}</footer>
    </main>
  </PublicShell>
</template>

<style scoped>
.faq-step-text :deep(.warn) {
  color: var(--red);
  font-weight: 700;
}
.faq-step-text :deep(.press) {
  color: var(--blue);
  font-weight: 700;
}
</style>
